import { Box, Grid, Stack, styled } from "@mui/material";
import { memo, useEffect, useMemo } from "react";
import { Row, Blocker } from "Components";
import { useIntl } from "react-intl";
import { DIRECT_PATHS } from "constants/routes";
import { useLocation, useParams } from "react-router-dom";
import ListingFilters from "Components/ListingFilters";
import { ParamsType } from "types/params";
import { COLORS } from "constants/layout";
import useBreakpoints from "hooks/useBreakpoint";
import Breadcrumbs from "Components/breadcrumbs";
import { useCommonData } from "providers/common-data";
import { Fragment, useState } from "react";
import { useSearchContext } from "context/SearchContext";
import useGetData from "hooks/useGetData";
import { removeNullFields } from "helpers/form";
import { LISTING_CARD_LIMIT } from "constants/layout";
import ListingFilterLayout from "layouts/listing-filter-layout";
import ResultText from "Components/text/ResultText";
import { ChipList } from "Components";
import SortChip from "Components/filters/SortChip";
import useUpdateSearchParams from "hooks/useUpdateSearchParams";
import { AppPagnigation } from "Components/Pagnigation";
import { QuickRFQForm } from "Components/form";
import { formatCurrency } from "helpers/string";
import { BlockType } from "types/common";
import FullySupplierSkeleton from "Components/skeleton/FullySupplierSkeleton";
import SponsorListingSupplier from "Components/list/SponsorListingSupplier";
import { apiGetSuppliersAndItems } from "services/api/listing";
import ListingItemCard from "Components/card/SupplierListing";
import useSearchParam from "hooks/useSearchParam";

type Props = {};

const SearchResult = (props: Props) => {
  const t = useIntl();
  const { isMobile } = useBreakpoints();
  const location = useLocation();
  const { pathname } = location;
  const { categoryId = "", countryId } = useParams<ParamsType>();
  const { breadscumbs = [], onChangeBreadscrumbs } = useCommonData();
  const { params, onUpdateParams, setCategoryLabel } = useSearchContext();
  const updateSearchParams = useUpdateSearchParams();
  const categoryClass = pathname.split("/")[1];
  const { searchParams } = useSearchParam();
  const keySearch = searchParams.get("keyword");

  // State for blockers
  const [blocker, setBlocker] = useState<BlockType>({
    is_blocked: true,
    required: "",
  });

  // Combined data fetch for suppliers
  const { isLoading, data } = useGetData({
    promise: () => {
      // Calculate the correct offset based on the current page
      const offset = (params.page - 1) * LISTING_CARD_LIMIT.category;

      // Helper to ensure value is always an array of strings
      const toStringArray = (val: any) => {
        if (Array.isArray(val)) return val.map(String);
        if (val !== undefined && val !== null && val !== "")
          return [String(val)];
        return [];
      };

      // Build filters object for the body
      const filters = {
        is_verified: toStringArray(params.filters?.is_verified),
        countries: toStringArray(params.filters?.countries),
        premium: toStringArray(params.filters?.premium),
        trust_star: toStringArray(params.filters?.trust_star),
      };

      // Build body object for POST
      const body = {
        category_id: categoryId || "",
        country: countryId || "",
        offset: offset,
        limit: LISTING_CARD_LIMIT.category,
        keyword: params.keyword || keySearch || "",
        sort: params.sort || "hot",
        filters,
      };

      return apiGetSuppliersAndItems(body);
    },
    deps: [params, categoryId, keySearch, countryId],
  });

  // Xử lý side effect khi data thay đổi
  useEffect(() => {
    if (!data) return;
    if (data.breadcrumb) {
      onChangeBreadscrumbs(data.breadcrumb);
    }
    setBlocker({
      is_blocked: data.is_blocked || false,
      required: data.required || "",
    });
    const label = categoryId ? data.category : data.title;
    setCategoryLabel(label);
  }, [data, categoryId, onChangeBreadscrumbs, setCategoryLabel]);

  // useMemo chỉ để tính toán giá trị trả về
  const { items, total, relatedCategories, categoryLabel } = useMemo(() => {
    if (!data) {
      return {
        items: [],
        total: 0,
        relatedCategories: [],
        categoryLabel: "",
      };
    }

    // Tạo lại biến label ở đây
    const label = categoryId ? data.category : data.title;

    // Process related categories
    const relatedCats = (data.related_categories || []).map(
      (category: any) => ({
        value: category.name,
        label: category.category_name,
      })
    );

    // Map items to ensure all fields are properly formatted
    const formattedItems = (data.data || []).map((item: any) => ({
      ...item,
      activity_tags: item.activity_tags || [],
      gallery_preview: item.gallery_preview || [],
      export_categories: item.export_categories || [],
      farm_avatar: item.farm_avatar || null,
      farm_item_image: item.farm_item_image || null,
      price_status: item.price_status || "",
      trust_star: item.trust_star || 0,
      count_review: item.count_review || 0,
      verified: item.verified || 0,
      is_verified_work_email: item.is_verified_work_email || 0,
      is_biz_verified: item.is_biz_verified || 0,
    }));

    return {
      items: formattedItems,
      total: data.total || 0,
      relatedCategories: relatedCats,
      categoryLabel: label,
    };
  }, [data, categoryId]);

  // Page totals
  const pageTotal = useMemo(
    () => Math.ceil((total || 0) / LISTING_CARD_LIMIT.category),
    [total]
  );

  // Pagination handler
  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    // Use both methods to ensure parameters are properly updated
    // 1. Update the context params
    onUpdateParams({
      page: value,
    });

    // 2. Update the URL search params directly
    updateSearchParams({
      page: value.toString(),
    });

    // Scroll to top when page changes
    window.scrollTo(0, 0);
  };

  // Sorting
  const onChangeSort = (value: string) => {
    // Update both context params and URL params to ensure data fetch is triggered
    onUpdateParams({
      sort: value,
      page: 1, // Reset to page 1 when sort changes
    });
    
    updateSearchParams({
      sort: value,
      page: "1", // Reset to page 1 when sort changes
    });
    
    // Scroll to top when sort changes
    window.scrollTo(0, 0);
  };

  const onClickCategory = (value: string) => {
    return DIRECT_PATHS.category(value);
  };

  const formInsertIndex = useMemo(() => {
    const itemLength = items.length;
    if (isMobile) {
      return itemLength > 4 ? 3 : -1;
    } else {
      return itemLength > 8 ? 7 : -1;
    }
  }, [items, isMobile]);

  return (
    <StyledGrid>
      <Breadcrumbs data={breadscumbs} />

      <ListingFilterLayout
        gap={1}
        sx={{
          mt: 1,
        }}
        leftComponent={<ListingFilters />}
        rightComponent={
          <Stack gap={1} sx={{ "& > *": { marginTop: "0 !important" } }}>
            <ResultText
              minHeight={18}
              component="h1"
              sx={{
                fontSize: {
                  xs: 14,
                  md: 16,
                },
              }}
            >
              {!isLoading
                ? t.formatMessage(
                    {
                      id:
                        items.length > 1
                          ? "value_results_for_value"
                          : "value_result_for_value",
                    },
                    {
                      qlt: (
                        <span className="qlt">
                          {formatCurrency(total)}
                          {total >= 10000 ? "+" : ""}
                        </span>
                      ),
                      value: (
                        <span
                          className="search"
                          style={{ color: COLORS.green[600] }}
                        >
                          {categoryId
                            ? categoryLabel
                            : keySearch || categoryLabel}
                        </span>
                      ),
                    }
                  )
                : ""}
            </ResultText>
            {isMobile && (
              <>
                <Row
                  className="content-header"
                  justifyContent="space-between"
                  my={1}
                >
                  <SortChip value={params?.sort} onChange={onChangeSort} />
                  <ListingFilters drawerMode />
                </Row>
              </>
            )}
            {!isMobile && (
              <SortChip value={params?.sort} onChange={onChangeSort} />
            )}
            <SponsorListingSupplier isNotShowButton={false} />

            <SectionContainer
              sx={{
                backgroundColor: COLORS.app.bg,
                marginTop: "0 !important",
              }}
            >
              {!blocker.is_blocked && (
                <div>
                  <Grid container spacing={1}>
                    {isLoading &&
                      Array.from(
                        new Array(Math.min(4, LISTING_CARD_LIMIT.category))
                      ).map((_, index) => (
                        <Grid item xs={12} md={12} key={index}>
                          <FullySupplierSkeleton />
                        </Grid>
                      ))}
                    {items.map((item: any, i: number) => (
                      <Fragment key={i}>
                        <Grid item xs={12} md={12}>
                          <ListingItemCard item={item} />
                        </Grid>
                        {i === formInsertIndex && (
                          <Grid xs={12} item>
                            <QuickRFQForm {...{ total, categoryLabel }} />
                          </Grid>
                        )}
                      </Fragment>
                    ))}
                    {!isLoading && formInsertIndex === -1 && (
                      <Grid xs={12} item>
                        <QuickRFQForm {...{ total, categoryLabel }} />
                      </Grid>
                    )}
                  </Grid>
                </div>
              )}
              <Blocker {...blocker} />
              {pageTotal > 1 && (
                <PaginationContainer>
                  <AppPagnigation
                    page={params.page}
                    onChange={handleChangePage}
                    count={pageTotal}
                  />
                </PaginationContainer>
              )}
            </SectionContainer>

            {relatedCategories.length > 0 && (
              <ChipList
                className="sm-card"
                chips={relatedCategories}
                selectedChip={categoryId}
                onDirectChip={onClickCategory}
                isLoading={isLoading}
              />
            )}
          </Stack>
        }
      />
    </StyledGrid>
  );
};

const StyledGrid = styled(Box)(({ theme }) => ({
  ".content-header": {
    backgroundColor: COLORS.app.bg,
    zIndex: 5,
  },
  [theme.breakpoints.down("md")]: {
    // Mobile styles
  },
}));

const SectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff",
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
  marginTop: 0,
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  display: "flex",
  justifyContent: "center",
}));

export default memo(SearchResult);
